import { useEffect } from "react";
import { Link } from "react-router";
import { Badge, Button } from "flowbite-react";
import { ShoppingCart, Trash2 } from "lucide-react";
import { useCartMutations } from "@/features/cart/hooks/useCartMutations";
import { useDealsCartMutations } from "@/features/deals/hooks/useDealsCartMutations";
import { useRentalBookingMutations } from "@/features/rentals/hooks/useRentalBookingMutations";
import { CartTypeSelector } from "@/shared/components/CartTypeSelector";
import { EuroFormat } from "@/shared/components/euroFormat";
import { CART_TYPE_LABELS } from "@/shared/constants/cart.constants";
import { useUnifiedCartContext } from "@/shared/context/UnifiedCartContext";
import { useIsFeatureHidden } from "@/shared/hooks/useAppSettings";
import {
  isBoutiqueCartItem,
  isDealsCartItem,
  isRentalsCartItem,
  type UnifiedCartItem,
} from "@/shared/types/unified-cart.type";

type CartDropdownProps = { onClose?: () => void };

export const CartDropdown = ({ onClose }: CartDropdownProps) => {
  const {
    cart,
    totalItems,
    totalAmount,
    isLoading,
    activeCartType,
    setActiveCartType,
    boutiqueItemCount,
    dealsItemCount,
    rentalsItemCount,
    originalBoutiqueCart,
    originalDealsCart,
  } = useUnifiedCartContext();

  // Calculate final amount after subsidies and cheques
  const finalAmount = (() => {
    if (activeCartType === "boutique" && originalBoutiqueCart) {
      return originalBoutiqueCart.total_amount_after_subsidy_and_cheque
        ?? originalBoutiqueCart.total_amount_after_subsidy
        ?? totalAmount;
    }
    if (activeCartType === "deals" && originalDealsCart) {
      return originalDealsCart.total_amount_after_subsidy ?? totalAmount;
    }
    return totalAmount;
  })();

  const { removeCartItem: removeBoutiqueItem } = useCartMutations();
  const { removeCartItem: removeDealsItem } = useDealsCartMutations({ showToasts: true });
  // « Supprimer » sur une location = annuler la résa cart côté API
  // (iso CartPage). Idempotent : si 422 (booking déjà annulé out-of-band),
  // le panier sera quand même rafraîchi via `onSettled` côté hook.
  const { cancel: cancelRentalBooking } = useRentalBookingMutations();

  const isDealsHidden = useIsFeatureHidden("service_deals");
  const isRentalsHidden = useIsFeatureHidden("service_catalog");

  useEffect(() => {
    if (isDealsHidden && activeCartType === "deals") {
      setActiveCartType("boutique");
    }
    if (isRentalsHidden && activeCartType === "rentals") {
      setActiveCartType("boutique");
    }
  }, [isDealsHidden, isRentalsHidden, activeCartType, setActiveCartType]);

  const handleRemoveItem = (item: UnifiedCartItem) => {
    if (isBoutiqueCartItem(item)) {
      const originalItem = item.originalItem;
      const removePayload = originalItem.item_type === "article"
        ? { article_id: originalItem.id }
        : originalItem.item_type === "offer"
          ? { offer_id: originalItem.id }
          : originalItem.item_type === "benefits"
            ? { benefits_offer_id: originalItem.id }
            : { rate_id: originalItem.id };
      removeBoutiqueItem.mutate(removePayload);
    }
    else if (isDealsCartItem(item)) {
      removeDealsItem.mutate({ referenceId: item.reference_id });
    }
    else if (isRentalsCartItem(item)) {
      cancelRentalBooking.mutate({ bookingId: item.id });
    }
  };

  if (isLoading) {
    return (
      <div className="w-[28rem] p-4">
        <p className="text-sm text-gray-500">Chargement...</p>
      </div>
    );
  }

  const hasAnyItems = boutiqueItemCount > 0 || dealsItemCount > 0 || rentalsItemCount > 0;

  return (
    <div className="w-[28rem]">
      {/* Cart type selector */}
      <CartTypeSelector
        activeType={activeCartType}
        onTypeChange={setActiveCartType}
        boutiqueCount={boutiqueItemCount}
        dealsCount={dealsItemCount}
        rentalsCount={rentalsItemCount}
        variant="header"
        showDealsTab={!isDealsHidden}
        showRentalsTab={!isRentalsHidden}
      />

      {/* Cart content */}
      {!cart || totalItems === 0
        ? (
          <div className="p-4 text-center">
            <ShoppingCart className="mx-auto mb-2 h-12 w-12 text-gray-400" />
            <p className="text-sm text-gray-500">
              {hasAnyItems
                ? `Votre panier ${CART_TYPE_LABELS[activeCartType]} est vide`
                : "Votre panier est vide"}
            </p>
          </div>
        )
        : (
          <>
            <div className="border-b border-gray-200 p-4 dark:border-gray-600">
              <h3 className="text-lg font-semibold text-foreground">
                Panier (
                {totalItems}
                {" "}
                {totalItems > 1 ? "articles" : "article"}
                )
              </h3>
            </div>

            <div className="max-h-96 overflow-y-auto">
              {cart.items.map((item) => {
                const subsidyDescription = isDealsCartItem(item)
                  ? item.originalItem.reference.subsidy_description
                  : null;
                // Voyage : les places sont déjà dans le sous-titre, la
                // quantité de la ligne (toujours 1) les contredirait.
                const isVoyage = isRentalsCartItem(item) && item.originalItem.item?.kind === "voyage";

                return (
                  <div
                    key={`${item.cartType}-${item.id}`}
                    className="border-b border-gray-100 p-4 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-700"
                  >
                    <div className="flex gap-3">
                      {item.image_url && (
                        <img
                          src={item.image_url}
                          alt={item.name}
                          className="h-16 w-16 rounded object-contain"
                        />
                      )}
                      <div className="min-w-0 flex-1">
                        <h4 className="truncate text-sm font-medium text-foreground">
                          {item.name}
                        </h4>
                        {isDealsCartItem(item) && (
                          <p className="font-mono text-[10px] text-gray-500 dark:text-gray-400">
                            #
                            {item.ref ?? "N/A"}
                          </p>
                        )}
                        {item.subtitle && (
                          <p className="text-xs text-gray-500">{item.subtitle}</p>
                        )}
                        {item.seat_details && item.seat_details.length > 0 && (
                          <div className="mt-0.5 flex flex-wrap gap-0.5">
                            {item.seat_details.map((seat, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center rounded bg-blue-50 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                              >
                                {seat.zone_name}
                                {" - R"}
                                {seat.row}
                                {" P"}
                                {seat.seat_number}
                              </span>
                            ))}
                          </div>
                        )}
                        {subsidyDescription && (
                          <Badge color="success" size="xs" className="w-fit mt-1">
                            {subsidyDescription}
                          </Badge>
                        )}
                        <div className="mt-2 flex items-center justify-between">
                          {!isVoyage && (
                            <span className="text-sm text-foreground">
                              Quantité&nbsp;:
                              {" "}
                              {item.quantity}
                            </span>
                          )}
                          <span className="ml-auto text-sm font-semibold text-foreground">
                            <EuroFormat amount={item.total_amount} />
                          </span>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(item)}
                        className="text-gray-400 hover:text-red-600 dark:hover:text-red-500"
                        aria-label={`Retirer «\u00A0${item.name}\u00A0» du panier`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="border-t border-gray-200 p-4 dark:border-gray-600">
              <div className="mb-3 flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">Total&nbsp;:</span>
                <div className="text-right">
                  {finalAmount < totalAmount && (
                    <span className="text-sm text-gray-400 line-through mr-2">
                      <EuroFormat amount={totalAmount} />
                    </span>
                  )}
                  <span className="text-lg font-bold text-foreground">
                    <EuroFormat amount={finalAmount} />
                  </span>
                </div>
              </div>
              <Button
                as={Link}
                to={`/boutique/panier?cartType=${activeCartType}`}
                color="primary"
                className="w-full"
                onClick={onClose}
              >
                Voir le panier
              </Button>
            </div>
          </>
        )}
    </div>
  );
};
